import { defineStore } from 'pinia';
import { computed, reactive } from 'vue';
import { TableContentHeader, TableContentProperty } from '@/corePagesModule/BaseTableContent/interface/tableContentHeaders';
import { TableContentItem } from '@/corePagesModule/BaseTableContent/interface/tableContentItems';
import { i18n } from '@/i18n';
import {
  DEFAULT_DISPLAY_FORECAST_MONTH,
  MAX_OF_DISPLAY_FORECAST_MONTH,
  MIN_OF_DISPLAY_FORECAST_MONTH,
  FORECAST_FOR_RECEIPT_ALL_BUSINESS_TABLE_TYPE,
} from '@/customerForecastModule/constants';
import { simpleCopy } from '@/utils/deepCopy';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { initPagingOptions, IPagingOptions } from '@/coreDomainsModule/paging';
import { FilterContentItem } from '@/corePagesModule/filterModal/filterModal';
import { GetAllCustomerForecastUseCase } from '@/customerForecastModule/useCases/getAllCustomerForecastUseCase';
import { CustomerForecastRepository } from '@/coreInfrastructureModule/Repositories/CustomerForecastRepository';
import { HEADER_MODIFY_TABLE_CONTENT_PROPERTY } from '@/corePagesModule/BaseTableContent/constants';
import { IYearMonth } from '@/coreDomainsModule/date';
import { IBusinessForCustomerForecast } from '@/coreDomainsModule/entities/businessForCustomerForecast';
import { SupplierForecastRepository } from '@/coreInfrastructureModule/Repositories/SupplierForecastRepository';
import { toast } from '@/corePagesModule/components/Toast/ToastManager';
import { openConfirmModal, openLoadingModal, openOkInformationModal } from '@/corePagesModule/components/modal/modalActions';
import { BulkUpdateBaseBusinessUserIdUseCase } from '@/customerForecastModule/useCases/bulkUpdateBaseBusinessUserIdUseCase';
import { BaseBusinessRepository } from '@/coreInfrastructureModule/Repositories/BaseBusinessRepository';
import { BulkPostSupplierForecastUseCase } from '@/customerForecastModule/useCases/bulkPostSupplierForecastUseCase';
import { IBusinessListForecastForReceiptState } from '../domains/entities/businessListForecastForReceipt';
import { ForecastForReceiptMenuItems, ForecastForReceiptFormState } from '../domains/entities/ForecastForReceiptMenu';
import { ForecastForReceiptMenuService } from '../domains/services/forecastForReceiptMenuService';
import { DownloadForecastForReceiptExcelUseCase } from '../useCases/downloadForecastForReceiptExcelUseCase';
import { saveAs } from 'file-saver';
import { useTableFilter } from '@/corePagesModule/composables/useTableFilter';
import { diContainer } from '@/coreContainer/container';
import { FilterService } from '@/coreDomainsModule/services/filterService';
import { useTablePreferences } from '@/corePagesModule/composables/useTablePreferences';

export const useBusinessListForecastForReceiptStore = defineStore('useBusinessListForecastForReceipt', () => {
  const t = i18n.t;

  const repository = new BaseBusinessRepository();

  // テーブルに表示する取引情報の取得
  const getAllCustomerForecastRepository = new CustomerForecastRepository();
  const getAllCustomerForecastUseCase = new GetAllCustomerForecastUseCase();

  /**
   * 表示する内示の月数の選択肢
   */
  const monthsOptionsDisplayForecastNumber = Array(MAX_OF_DISPLAY_FORECAST_MONTH - MIN_OF_DISPLAY_FORECAST_MONTH + 1)
    .fill(0)
    .map((_, i) => i + MIN_OF_DISPLAY_FORECAST_MONTH);

  const state = reactive<IBusinessListForecastForReceiptState>({
    items: [],
    tableContents: {
      renderingKey: 0,
      tableHeaders: {} as TableContentHeader,
      checkedDisplayIds: [],
      loadingState: {
        status: {
          type: 'none',
        },
      },
      pagingOptions: initPagingOptions(30),
      sortKey: undefined,
      sortTargetYearMonth: undefined,
      isAsc: true,
    },
    editForecastModalState: false,
    staffNameList: [],
    detailFilterModalState: false,
    displayForecastsMonth: DEFAULT_DISPLAY_FORECAST_MONTH,
    createForecastModalState: false,
    receivedForecastYearMonthInfo: { year: 0, month: 0 },
    createSupplierForecastLoadingState: {
      status: {
        type: 'none',
      },
    },
    confirmApplyEditModalState: false,
    pageMode: 'normal',
    isOpenForecastForReceiptMenuDropdown: false,
    selectedForecastForReceiptMenu: undefined,
    editForecastForReceiptModeState: {
      updateForecastForReceiptModalState: false,
      selectedEditForecastForReceiptId: '',
      selectedForecastForReceiptFormState: {
        forecastForReceiptName: '',
        forecastForReceiptResponsibleUserIdList: [],
        outputRange: 0,
      },
    },
    forecastForReceiptLoadingState: {
      type: 'none',
      state: {
        status: {
          type: 'none',
        },
      },
    },
    deleteForecastForReceiptConfirmModalState: false,
    downloadForecastForReceiptModalState: {
      modalState: false,
      loadingState: {
        status: {
          type: 'none',
        },
      },
    },
    downloadCustomerForecastModalState: {
      modalState: false,
      startYearMonth: { year: 0, month: 0 },
      endYearMonth: { year: 0, month: 0 },
    },
  });

  const { cachedFilterItems, bulkUpdateFilterInfos, getFilterItemsValue, initTableFilter } = useTableFilter(
    'forecastForReceiptAllBusinessTable'
  );

  const { initTablePreferences, savePageSize, saveSort } = useTablePreferences();

  const tableContentItems = computed((): TableContentItem[] | undefined => {
    if (!state.items) return undefined;
    return state.items.map((item) => {
      return {
        displayId: item.businessId ?? '', // businessIdを表示用の一意のIDとして使用する
        isVirtualChild: false,
        ...item,
      };
    });
  });

  /**
   * stateの初期化
   */
  const $reset = () => {
    state.items = [];
    state.tableContents = {
      renderingKey: 0,
      tableHeaders: {} as TableContentHeader,
      checkedDisplayIds: [],
      loadingState: {
        status: {
          type: 'none',
        },
      },
      pagingOptions: initPagingOptions(30),
      sortKey: undefined,
      sortTargetYearMonth: undefined,
      isAsc: true,
    };
    state.editForecastModalState = false;
    state.staffNameList = [];
    state.detailFilterModalState = false;
    state.displayForecastsMonth = DEFAULT_DISPLAY_FORECAST_MONTH;
    state.createForecastModalState = false;
    state.receivedForecastYearMonthInfo = DateService.getBeforeYearMonth(DateService.convertDateToIYearMonth(new Date())); // デフォルトは先月
    state.isOpenSelectInputType = undefined;
    state.createSupplierForecastLoadingState = {
      status: {
        type: 'none',
      },
    };
    state.confirmApplyEditModalState = false;
    state.selectedCustomerForecastUserIdList = undefined;
    state.pageMode = 'normal';
    state.isOpenForecastForReceiptMenuDropdown = false;
    state.selectedForecastForReceiptMenu = undefined;
    state.editForecastForReceiptModeState = {
      updateForecastForReceiptModalState: false,
      selectedEditForecastForReceiptId: '',
      selectedForecastForReceiptFormState: {
        forecastForReceiptName: '',
        forecastForReceiptResponsibleUserIdList: [],
        outputRange: 0,
      },
    };
    state.forecastForReceiptLoadingState = {
      type: 'none',
      state: {
        status: {
          type: 'none',
        },
      },
    };
    state.deleteForecastForReceiptConfirmModalState = false;
    state.downloadForecastForReceiptModalState = {
      modalState: false,
      loadingState: {
        status: {
          type: 'none',
        },
      },
    };
    state.downloadCustomerForecastModalState = {
      modalState: false,
      startYearMonth: { year: 0, month: 0 },
      endYearMonth: { year: 0, month: 0 },
    };
  };

  /**
   * テーブル表示用綱目のみリセット
   */
  const resetAllBusinessTableContentItems = () => {
    state.tableContents.tableHeaders = {} as TableContentHeader;
    state.items = [];
    state.tableContents.checkedDisplayIds = [];
    state.tableContents.loadingState = {
      status: {
        type: 'none',
      },
    };
  };

  /**
   * テーブルの選択状態をリセットする
   */
  const resetTableCheckedState = () => {
    state.tableContents.checkedDisplayIds = [];
    state.tableContents.renderingKey += 1;
  };

  /**
   * 初期化処理
   */
  const init = async () => {
    $reset();
    updateDisplayBusinesses();
  };

  /**
   * 全取引テーブルのヘッダー情報を取得する
   * @param forecastCount - 表示する内示の数
   * @returns ヘッダー情報
   */
  const fetchAllBusinessTableHeaders = (forecastCount: number): TableContentHeader => {
    const forecasts: TableContentProperty[] = [];
    for (let i = 0; i < forecastCount; i++) {
      forecasts.push({
        key: `customerAnnounceForReceipt-${i + 1}`,
        isVariableWidth: true,
        isNarrowable: true,
        isEditable: false,
        sortOption: {
          isSortable: true,
          isAsc: true,
        },
        propertyPath: { paths: [], propertyType: 'number' },
      });
    }
    return {
      contentProperties: [
        {
          key: 'choice',
          isVariableWidth: false,
          isNarrowable: false,
          isEditable: false,
          sortOption: { isSortable: false, isAsc: true },
          propertyPath: { paths: ['choice'], propertyType: 'string' },
        },
        {
          key: 'ForecastForReceiptStaffFull',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['ForecastForReceiptStaffFull'], propertyType: 'string' },
        },
        {
          key: 'ForecastForReceiptName',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['ForecastForReceiptName'], propertyType: 'string' },
        },
        {
          key: 'ForecastForReceiptResponsibleUser',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['ForecastForReceiptResponsibleUser'], propertyType: 'string' },
        },
        {
          key: 'supplierNumber',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['supplierNumber'], propertyType: 'string' },
        },
        {
          key: 'supplierName',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['supplierName'], propertyType: 'string' },
        },
        {
          key: 'manufacturer',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['manufacturer'], propertyType: 'string' },
        },
        {
          key: 'businessNumber',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['businessNumber'], propertyType: 'string' },
        },
        {
          key: 'businessName',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['businessName'], propertyType: 'string' },
        },
        {
          key: 'package',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['package'], propertyType: 'number' },
        },
        {
          key: 'unit',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['unit'], propertyType: 'string' },
        },
        {
          key: 'warehouseNumber',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['warehouseNumber'], propertyType: 'string' },
        },
        {
          key: 'warehouse',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['warehouse'], propertyType: 'string' },
        },
        {
          key: 'endUser',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['endUser'], propertyType: 'string' },
        },
        {
          key: 'customerNumber',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['customerNumber'], propertyType: 'string' },
        },
        {
          key: 'customerName',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['customerName'], propertyType: 'string' },
        },
        {
          key: 'salesDestinationNumber',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['salesDestinationNumber'], propertyType: 'string' },
        },
        {
          key: 'salesDestinationName',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['salesDestinationName'], propertyType: 'string' },
        },
        {
          key: 'salesUnitPrice',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['salesUnitPrice'], propertyType: 'number' },
        },
        {
          key: 'ForecastForReceiptReceiveDate',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['ForecastForReceiptReceiveDate'], propertyType: 'string' },
        },
        {
          key: 'SupplierForecastCreateStatus',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['SupplierForecastCreateStatus'], propertyType: 'string' },
        },
        {
          key: 'Space8px',
          isVariableWidth: false,
          isNarrowable: false,
          isEditable: false,
          sortOption: {
            isSortable: false,
            isAsc: true,
          },
          headerWidth: 8,
          propertyPath: { paths: [], propertyType: 'string' },
        },
        ...forecasts,
        ...(state.pageMode === 'editForecastForReceipt'
          ? [
              {
                key: 'removeFromForecastButton',
                isVariableWidth: false,
                isNarrowable: false,
                isEditable: false,
                sortOption: { isSortable: false, isAsc: true },
                headerWidth: 40,
                propertyPath: { paths: [] as string[], propertyType: 'string' as const },
              },
            ]
          : []),
        HEADER_MODIFY_TABLE_CONTENT_PROPERTY,
      ],
      options: {
        stableColumnCount: 0,
        resizableHeaderColumn: false,
      },
      forecastStartYearMonth: DateService.getShiftedIYearMonthByMonths(
        state.receivedForecastYearMonthInfo.year,
        state.receivedForecastYearMonthInfo.month,
        1
      ), // 受領月の翌月
    };
  };

  /**
   * 受領用内示に紐づく取引情報の取得
   */
  const fetchDisplayBusinesses = async () => {
    if (state.receivedForecastYearMonthInfo.year === 0 || state.receivedForecastYearMonthInfo.month === 0) return;

    state.tableContents.loadingState = {
      status: {
        type: 'loading',
      },
    };

    await initTableFilter();
    await initTablePreferences(FORECAST_FOR_RECEIPT_ALL_BUSINESS_TABLE_TYPE, state, initPagingOptions);

    // 受領用内示についてのフィルター項目を追加する
    const useFilterContentInfo = simpleCopy(cachedFilterItems.value);
    if (
      state.editForecastForReceiptModeState.selectedEditForecastForReceiptId !== '' &&
      state.editForecastForReceiptModeState.updateForecastForReceiptModalState != null
    ) {
      useFilterContentInfo.push(
        ForecastForReceiptMenuService.getFilterContentItem(state.editForecastForReceiptModeState.selectedEditForecastForReceiptId)
      );
    }

    // バックエンドに渡すソートキーを変換
    const backendSortKey = convertUiKeyToBackendKey(state.tableContents.sortKey);

    const result = await getAllCustomerForecastUseCase.Execute(
      getAllCustomerForecastRepository,
      state.receivedForecastYearMonthInfo,
      state.tableContents.pagingOptions,
      backendSortKey,
      state.tableContents.sortTargetYearMonth,
      state.tableContents.isAsc,
      useFilterContentInfo
    );

    switch (result.status.type) {
      case 'success':
        state.items = result.status.data.result;
        state.tableContents.pagingOptions = result.status.data.pagingOptions;

        if ((result.status.data.pagingOptions?.returnCount ?? 0) === 0) {
          state.tableContents.loadingState.status = {
            type: 'zeroMatch',
          };
        } else {
          state.tableContents.loadingState.status.type = 'success';
        }
        break;
      case 'error':
        state.tableContents.loadingState = {
          status: {
            type: 'error',
            errorMessage: t('データ取得に失敗しました'),
          },
        };
        console.log('error');
        break;
    }
  };

  /**
   * テーブルに表示する取引情報を更新する
   */
  const updateDisplayBusinesses = async () => {
    resetAllBusinessTableContentItems();
    // 受領用内示が選択されている場合、ヘッダーの更新と取引情報の取得を行う
    state.tableContents.tableHeaders = fetchAllBusinessTableHeaders(state.displayForecastsMonth);
    await fetchDisplayBusinesses();
    state.tableContents.renderingKey += 1;
  };

  /**
   * 選択された受領用内示情報を持つstate変数の情報を更新する
   * @param forecastForReceiptFormState - 受領用内示情報
   */
  const updateSelectedForecastForReceiptFormState = (forecastForReceiptFormState?: ForecastForReceiptFormState) => {
    // モーダルのフォームにセットする値
    state.editForecastForReceiptModeState.selectedForecastForReceiptFormState.forecastForReceiptName =
      forecastForReceiptFormState?.forecastForReceiptName ?? '';
    state.editForecastForReceiptModeState.selectedForecastForReceiptFormState.forecastForReceiptResponsibleUserIdList =
      forecastForReceiptFormState?.forecastForReceiptResponsibleUserIdList ?? [];
    state.editForecastForReceiptModeState.selectedForecastForReceiptFormState.outputRange = forecastForReceiptFormState?.outputRange ?? 0;
  };

  /**
   * 内示の編集を適用する
   * @param selectedCustomerForecastUserIdList - 選択された担当者名リスト
   */
  const applyEditForecast = async (selectedCustomerForecastUserIdList: string[]) => {
    if (!state.tableContents.checkedDisplayIds) return;

    // ローディング開始
    state.tableContents.loadingState.status.type = 'loading';

    const useCase = new BulkUpdateBaseBusinessUserIdUseCase();
    // 担当者の変更処理
    const result = await useCase.Execute(repository, state.tableContents.checkedDisplayIds, selectedCustomerForecastUserIdList);

    switch (result.status.type) {
      case 'success':
        // ローディング終了
        state.tableContents.loadingState.status.type = 'success';
        // チェックを外す
        state.tableContents.checkedDisplayIds = [];
        // 成功トースト表示
        toast.success(t('担当者を変更しました。'));
        // テーブルの再描画
        await updateDisplayBusinesses();
        break;
      case 'error':
        state.tableContents.loadingState.status.type = 'error';
        console.error('applyEditForecast error');
        // エラートースト表示
        toast.error(t('担当者の変更に失敗しました。'));
        break;
    }
  };

  /**
   * 表示する内示の月数を選択した際の処理
   * @param selectedIndex - 選択肢のインデックス
   */
  const onSelectedDisplayForecastsMonth = async (selectedIndex: number) => {
    if (selectedIndex === undefined) return;
    state.displayForecastsMonth = selectedIndex + MIN_OF_DISPLAY_FORECAST_MONTH;
    // 内示の列数が変わるためヘッダーも更新
    await updateDisplayBusinesses();
  };

  /**
   * 仕入先内示を作成する
   * @param repository - リポジトリ
   * @param createdSupplierForecastYearMonth - 仕入先内示の作成年月
   * @param startTargetYearMonth - 内示対象月の開始月
   * @param targetYearMonthCount - 内示対象月の数
   */
  const createSupplierForecast = async (
    repository: SupplierForecastRepository,
    createdSupplierForecastYearMonth: IYearMonth,
    startTargetYearMonth: IYearMonth,
    targetYearMonthCount: number
  ) => {
    // モーダルを閉じる
    state.createForecastModalState = false;
    // ローディング開始
    state.createSupplierForecastLoadingState.status.type = 'loading';

    const useCase = new BulkPostSupplierForecastUseCase();

    // チェックが付いているアイテムについて、客先内示=仕入先内示として登録
    const result = await useCase.Execute(
      repository,
      createdSupplierForecastYearMonth,
      startTargetYearMonth,
      targetYearMonthCount,
      cachedFilterItems.value
    );

    switch (result.status.type) {
      case 'success':
        // ローディング終了
        state.createSupplierForecastLoadingState.status.type = 'success';
        // 成功トースト表示
        toast.success(t('ステータスを一括修正しました。'));
        // テーブルの再描画
        await updateDisplayBusinesses();
        break;

      case 'error':
        const errorResult = result.status.error;
        if (errorResult['code'] === 'RESOURCE_CONFLICT') {
          // 重複エラーの場合の処理
          // 一度、ローディングを終了
          state.createSupplierForecastLoadingState.status.type = 'success';
          await _upsertConfirmSupplierAnnounce(repository, createdSupplierForecastYearMonth, startTargetYearMonth, targetYearMonthCount);
        } else {
          // その他のエラーの場合の処理
          state.createSupplierForecastLoadingState.status.type = 'error';
          console.error('createSupplierForecast error');
          // エラートースト表示
          toast.error(t('ステータスの一括修正に失敗しました。'));
        }
        break;
    }
  };

  /**
   * ページング変更時の処理
   * @param pagingOptions - ページングオプション
   */
  const onChangePaging = async (pagingOptions: IPagingOptions) => {
    state.tableContents.pagingOptions = {
      pageSize: pagingOptions.pageSize,
      offset: pagingOptions.offset,
      totalResultsAvailable: pagingOptions.totalResultsAvailable,
      returnCount: pagingOptions.returnCount,
    };

    // ページサイズ設定の永続化対応
    await savePageSize(FORECAST_FOR_RECEIPT_ALL_BUSINESS_TABLE_TYPE, state.tableContents.pagingOptions.pageSize);
    await fetchDisplayBusinesses();
  };

  /**
   * UIヘッダーキーをバックエンドのカラム名に変換するヘルパー関数
   * @param uiKey - UIヘッダーキー
   * @returns バックエンドのカラム名、または変換不要な場合は元のキー
   */
  const convertUiKeyToBackendKey = (uiKey?: string): string | undefined => {
    if (uiKey?.includes('customerAnnounceForReceipt')) {
      return 'customerForecastAmount';
    } else if (uiKey === 'ForecastForReceiptStaffFull') {
      return 'baseBusinessResponsibleUser';
    } else if (uiKey === 'ForecastForReceiptReceiveDate') {
      return 'updateAt';
    } else {
      return uiKey;
    }
  };

  /**
   * テーブルのデータをソートする
   * @param sortKey - ソートキー
   */
  const sortTableContentItems = async (sortKey: TableContentProperty) => {
    if (sortKey.key === state.tableContents.sortKey) {
      state.tableContents.isAsc = !state.tableContents.isAsc;
    } else {
      state.tableContents.isAsc = false;
    }
    state.tableContents.sortKey = sortKey.key;
    // ソート設定の永続化対応
    await saveSort(FORECAST_FOR_RECEIPT_ALL_BUSINESS_TABLE_TYPE, state.tableContents.sortKey, state.tableContents.isAsc);

    // UIのソート表示を更新
    state.tableContents.tableHeaders.contentProperties = state.tableContents.tableHeaders.contentProperties.map((property) => {
      const currentIsAsc = state.tableContents.isAsc ?? true;
      property.sortOption.isAsc = property.key === state.tableContents.sortKey ? currentIsAsc : true; // ソート対象外は上向き
      return property;
    });
    state.tableContents.pagingOptions.offset = 0;
    await fetchDisplayBusinesses();
  };

  /**
   * 詳細フィルターの適用処理
   * @param detailFilterItem - 詳細フィルターの選択内容
   */
  const onAppliedDetailFilter = async (detailFilterItem: FilterContentItem[]) => {
    await bulkUpdateFilterInfos(simpleCopy(detailFilterItem));
    state.detailFilterModalState = false;
    await fetchDisplayBusinesses();
    resetTableCheckedState();
  };

  /**
   * 受領用内示編集の変更内容をを適用する
   * @param selectedStaffIdList - 選択されたスタッフ名のリスト
   */
  const onApplyEditForecast = async (selectedStaffIdList?: string[]) => {
    // 編集モーダルを閉じる
    state.editForecastModalState = false;

    // 担当者が選択されていない場合は何もしない
    if (!selectedStaffIdList || selectedStaffIdList.length < 1) return;

    state.selectedCustomerForecastUserIdList = selectedStaffIdList;

    // 確認モーダルを表示
    state.confirmApplyEditModalState = true;
  };

  /**
   * 編集適用確認モーダルで確定ボタンを押した際、編集適用処理を行う
   */
  const onAppliedConfirmApplyEdit = async () => {
    if (!state.confirmApplyEditModalState || !state.selectedCustomerForecastUserIdList) return;
    // 確認モーダルを閉じる
    state.confirmApplyEditModalState = false;
    // 編集適用処理
    await applyEditForecast(state.selectedCustomerForecastUserIdList);
  };

  /**
   * 編集適用確認モーダルでキャンセルボタンを押した際、編集画面を再表示する
   */
  const onCanceledConfirmApplyEdit = () => {
    if (!state.confirmApplyEditModalState) return;
    // 確認モーダルを閉じる
    state.confirmApplyEditModalState = false;
    // 再度編集画面を表示
    state.editForecastModalState = true;
  };

  /**
   * 編集適用確認モーダルで編集適用を取り消すボタンを押した際、チェックを外して元の状態に戻す
   */
  const onRevokedConfirmApplyEdit = () => {
    if (!state.confirmApplyEditModalState) return;
    // 確認モーダルを閉じる
    state.confirmApplyEditModalState = false;
    // チェックを外す
    state.tableContents.checkedDisplayIds = [];
    // レンダリング
    state.tableContents.renderingKey += 1;
  };

  /**
   * 内示の受領月を選択した際の処理
   * @param selectedYearMonth - 選択された年月
   */
  const onSelectedReceiveMonth = async (selectedYearMonth: IYearMonth) => {
    state.receivedForecastYearMonthInfo = selectedYearMonth;
    if (selectedYearMonth.year === 0 || selectedYearMonth.month === 0) return;
    await updateDisplayBusinesses();
  };

  /**
   * 仕入先内示を作成する際の確認モーダルで確定ボタンを押した際の処理
   * @param repository - リポジトリ
   */
  const onConfirmedCreateForecast = async (repository: SupplierForecastRepository) => {
    if (state.items.length === 0) return;

    // 客先内示の受領年月 = 仕入先内示の作成年月とする
    const selectedYearMonth = state.receivedForecastYearMonthInfo;
    // min: 作成年月、max: 作成年月の翌月+表示月数 の範囲で作成するため+1する
    const createMonthCount = state.displayForecastsMonth + 1;

    await createSupplierForecast(repository, selectedYearMonth, selectedYearMonth, createMonthCount);
  };

  /**
   * チェックボックスの選択状態を更新する
   * @param checkedDisplayIds - チェックされたアイテムのID
   */
  const onCheckedDisplayIds = (checkedDisplayIds: string[]) => {
    state.tableContents.checkedDisplayIds = checkedDisplayIds;
  };

  // ↓ 受領用内示関連

  /**
   * 受領用内示メニューが選択された際の処理
   * @param forecastForReceiptMenu - 選択された受領用内示メニュー
   */
  const onSelectedForecastForReceiptMenu = (forecastForReceiptMenu: ForecastForReceiptMenuItems) => {
    state.selectedForecastForReceiptMenu = forecastForReceiptMenu;
  };

  /**
   * 受領用内示関連のモーダルが閉じられた際の処理
   */
  const onClosedForecastForReceiptActionModal = () => {
    state.selectedForecastForReceiptMenu = undefined;
  };

  /**
   * 受領用内示新規作成を適用した際の処理
   * @param newForecastForReceiptInfo - 新規作成する受領用内示の情報
   */
  const onAppliedCreateBaseBusinessForecastForReceipt = async (newForecastForReceiptInfo: ForecastForReceiptFormState) => {
    // メニューの選択を解除
    state.selectedForecastForReceiptMenu = undefined;

    // ローディング開始
    state.forecastForReceiptLoadingState.state.status.type = 'loading';
    state.forecastForReceiptLoadingState.type = 'create';

    // 受領用内示作成処理
    const result = await diContainer.createForecastForReceiptUseCase.Execute(newForecastForReceiptInfo);

    switch (result.status.type) {
      case 'success':
        // ローディング終了
        state.forecastForReceiptLoadingState.state.status.type = 'success';
        state.forecastForReceiptLoadingState.type = 'none';
        // 成功トーストを表示
        toast.success(t('受領用内示の作成に成功しました。'));
        break;
      case 'error':
      default:
        state.forecastForReceiptLoadingState.state.status.type = 'error';
        state.forecastForReceiptLoadingState.type = 'none';
        // 失敗トーストを表示
        toast.error(t('受領用内示の作成に失敗しました。'));
        break;
    }
  };

  /**
   * 受領用内示追加を適用した際の処理
   * @param targetForecastForReceiptId - 追加対象の受領用内示ID
   */
  const onAppliedAddBaseBusinessForecastForReceipt = async (targetForecastForReceiptId: string) => {
    // メニューの選択を解除
    state.selectedForecastForReceiptMenu = undefined;

    // ローディング開始
    state.forecastForReceiptLoadingState.state.status.type = 'loading';
    state.forecastForReceiptLoadingState.type = 'addBaseBusiness';

    // 追加処理
    const result = await diContainer.addBaseBusinessForecastForReceiptUseCase.Execute(
      state.tableContents.checkedDisplayIds,
      targetForecastForReceiptId
    );

    switch (result.status.type) {
      case 'success':
        // 受領用内示用のローディング終了
        state.forecastForReceiptLoadingState.state.status.type = 'success';
        state.forecastForReceiptLoadingState.type = 'none';
        // 成功トーストを表示
        toast.success(t('受領用内示への追加に成功しました。'));
        // 受領用内示追加後のデータを取得
        await updateDisplayBusinesses();
        // チェック状態をリセット
        state.tableContents.checkedDisplayIds = [];
        break;
      case 'error':
      default:
        state.forecastForReceiptLoadingState.state.status.type = 'error';
        state.forecastForReceiptLoadingState.type = 'none';
        // 失敗トーストを表示
        toast.error(t('受領用内示への追加に失敗しました。'));
        break;
    }
  };

  /**
   * 受領用内示編集を適用した際の処理
   * @param selectedForecastForReceiptId - 編集対象の受領用内示ID
   * @param selectedForecastForReceiptFormState - 編集対象の受領用内示情報
   */
  const onAppliedEditBaseBusinessForecastForReceipt = async (
    selectedForecastForReceiptId: string,
    selectedForecastForReceiptFormState?: ForecastForReceiptFormState
  ) => {
    // メニューの選択を解除
    state.selectedForecastForReceiptMenu = undefined;

    state.editForecastForReceiptModeState.selectedEditForecastForReceiptId = selectedForecastForReceiptId;

    // モーダルのフォームにセットする値
    updateSelectedForecastForReceiptFormState(selectedForecastForReceiptFormState);

    // 編集モードに遷移
    state.pageMode = 'editForecastForReceipt';

    // チェック状態をリセット
    state.tableContents.checkedDisplayIds = [];

    await updateDisplayBusinesses();
  };

  /**
   * 受領用内示削除を適用した際の処理
   * @param targetForecastForReceiptId - 削除対象の受領用内示ID
   */
  const onAppliedDeleteBaseBusinessForecastForReceipt = async (targetForecastForReceiptId: string) => {
    // メニューの選択を解除
    state.selectedForecastForReceiptMenu = undefined;

    // ローディング開始
    state.forecastForReceiptLoadingState.state.status.type = 'loading';
    state.forecastForReceiptLoadingState.type = 'delete';

    // 削除処理
    const result = await diContainer.deleteForecastForReceiptUseCase.Execute(targetForecastForReceiptId);

    switch (result.status.type) {
      case 'success':
        // 受領用内示用のローディング終了
        state.forecastForReceiptLoadingState.state.status.type = 'success';
        state.forecastForReceiptLoadingState.type = 'none';
        // 成功トーストを表示
        toast.success(t('受領用内示削除に成功しました。'));
        // 受領用内示削除後のデータを取得
        await updateDisplayBusinesses();
        break;
      case 'error':
      default:
        state.forecastForReceiptLoadingState.state.status.type = 'error';
        state.forecastForReceiptLoadingState.type = 'none';
        // 失敗トーストを表示
        toast.error(t('受領用内示削除に失敗しました。'));
        break;
    }
  };

  /**
   * 受領用内示名編集を適用した際の処理
   * @param updateForecastForReceiptInfo - 編集後の受領用内示名
   */
  const onAppliedUpdateBaseBusinessForecastForReceipt = async (updateForecastForReceiptInfo: ForecastForReceiptFormState) => {
    // モーダルを閉じる
    state.editForecastForReceiptModeState.updateForecastForReceiptModalState = false;

    if (state.editForecastForReceiptModeState.selectedEditForecastForReceiptId === '') {
      // エラートーストを表示
      toast.error(t('受領用内示編集に失敗しました。'));
      return;
    }

    // ローディング開始
    state.forecastForReceiptLoadingState.state.status.type = 'loading';
    state.forecastForReceiptLoadingState.type = 'updateForecastForReceipt';

    // 編集処理
    const result = await diContainer.updateForecastForReceiptUseCase.Execute(
      state.editForecastForReceiptModeState.selectedEditForecastForReceiptId,
      updateForecastForReceiptInfo
    );

    switch (result.status.type) {
      case 'success':
        // ローディング終了
        state.forecastForReceiptLoadingState.state.status.type = 'success';
        state.forecastForReceiptLoadingState.type = 'none';
        // 編集処理が成功した場合、リクエストで投げた値をモーダルのフォームにセットする
        state.editForecastForReceiptModeState.selectedForecastForReceiptFormState = simpleCopy(updateForecastForReceiptInfo);
        // 成功トーストを表示
        toast.success(t('受領用内示編集に成功しました。'));
        break;
      case 'error':
      default:
        state.forecastForReceiptLoadingState.state.status.type = 'error';
        state.forecastForReceiptLoadingState.type = 'none';
        // 失敗トーストを表示
        toast.error(t('受領用内示編集に失敗しました。'));
        break;
    }
  };

  //   ↓ 受領用内示編集モード

  /**
   * 受領用内示編集モードで受領用内示を選択した際の処理
   * @param forecastForReceiptId - 受領用内示ID
   */
  const onSwitchedEditTargetForecastForReceipt = async (forecastForReceiptId: string | undefined) => {
    if (!forecastForReceiptId) {
      // 受領用内示未選択の時 ^ 編集モードの時は編集モードを解除
      setNormalMode();
    }
    state.editForecastForReceiptModeState.selectedEditForecastForReceiptId = forecastForReceiptId ?? '';
    // 情報を再取得
    await updateDisplayBusinesses();
  };

  /**
   * 受領用内示編集モードで受領用内示から外すボタンが押された際の処理
   */
  const onClickedDeleteFromForecastForReceiptButton = async () => {
    if (state.editForecastForReceiptModeState.selectedEditForecastForReceiptId === '' || state.tableContents.checkedDisplayIds.length === 0)
      return;

    // ローディング開始
    state.forecastForReceiptLoadingState.state.status.type = 'loading';
    state.forecastForReceiptLoadingState.type = 'deleteBaseBusiness';

    const result = await diContainer.deleteBaseBusinessForecastForReceiptUseCase.Execute(
      state.tableContents.checkedDisplayIds,
      state.editForecastForReceiptModeState.selectedEditForecastForReceiptId
    );

    switch (result.status.type) {
      case 'success':
        // 受領用内示用のローディング終了
        state.forecastForReceiptLoadingState.state.status.type = 'success';
        state.forecastForReceiptLoadingState.type = 'none';
        // 成功トーストを表示
        toast.success(t('受領用内示から取引を外しました。'));
        // 受領用内示削除後のデータを取得
        await updateDisplayBusinesses();
        // チェック状態をリセット
        state.tableContents.checkedDisplayIds = [];
        break;
      case 'error':
      default:
        state.forecastForReceiptLoadingState.type = 'none';
        // 失敗トーストを表示
        toast.error(t('受領用内示から取引を外せませんでした。'));
        break;
    }
  };

  /**
   * 受領用内示編集モードから通常モードに戻る際の処理
   */
  const onBackToNormalMode = async () => {
    console.log('onBackToNormalMode');
    // ページモードを通常モードに戻す
    setNormalMode();
    // 選択中の受領用内示IDをリセット
    state.editForecastForReceiptModeState.selectedEditForecastForReceiptId = '';
    // チェック状態をリセット
    state.tableContents.checkedDisplayIds = [];
    // 情報を再取得
    await updateDisplayBusinesses();
  };

  /**
   * 画面モードを通常モードに設定する
   */
  const setNormalMode = () => {
    state.pageMode = 'normal';
  };

  /**
   * 受領用内示編集モードで受領用内示削除ボタンが押された際の処理
   * 確認モーダルを表示する
   */
  const onClickedDeleteForecastForReceiptInEditMode = () => {
    state.deleteForecastForReceiptConfirmModalState = true;
  };

  /**
   * 受領用内示編集モードでの削除確認モーダルで確定ボタンが押された際の処理
   */
  const onConfirmedDeleteForecastForReceiptInEditMode = async () => {
    // 確認モーダルを閉じる
    state.deleteForecastForReceiptConfirmModalState = false;

    const targetId = state.editForecastForReceiptModeState.selectedEditForecastForReceiptId;
    if (!targetId) return;

    // ローディング開始
    state.forecastForReceiptLoadingState.state.status.type = 'loading';
    state.forecastForReceiptLoadingState.type = 'delete';

    // 削除処理
    const result = await diContainer.deleteForecastForReceiptUseCase.Execute(targetId);

    switch (result.status.type) {
      case 'success':
        // 受領用内示用のローディング終了
        state.forecastForReceiptLoadingState.state.status.type = 'success';
        state.forecastForReceiptLoadingState.type = 'none';
        // 成功トーストを表示
        toast.success(t('受領用内示削除に成功しました。'));
        // 通常モードに戻す
        setNormalMode();
        state.editForecastForReceiptModeState.selectedEditForecastForReceiptId = '';
        state.tableContents.checkedDisplayIds = [];
        // データを再取得
        await updateDisplayBusinesses();
        break;
      case 'error':
      default:
        state.forecastForReceiptLoadingState.state.status.type = 'error';
        state.forecastForReceiptLoadingState.type = 'none';
        // 失敗トーストを表示
        toast.error(t('受領用内示削除に失敗しました。'));
        break;
    }
  };

  /**
   * 受領用内示削除確認モーダルのキャンセル処理
   */
  const onCanceledDeleteForecastForReceiptInEditMode = () => {
    state.deleteForecastForReceiptConfirmModalState = false;
  };

  /**
   * 受領用内示編集モードでインライン削除ボタン（✕）が押された際の処理
   * @param displayId - 削除対象の取引のdisplayId
   */
  const onClickRemoveFromForecastInRow = async (displayId: string) => {
    if (state.editForecastForReceiptModeState.selectedEditForecastForReceiptId === '' || !displayId) return;

    // ローディング開始
    state.forecastForReceiptLoadingState.state.status.type = 'loading';
    state.forecastForReceiptLoadingState.type = 'deleteBaseBusiness';

    const result = await diContainer.deleteBaseBusinessForecastForReceiptUseCase.Execute(
      [displayId],
      state.editForecastForReceiptModeState.selectedEditForecastForReceiptId
    );

    switch (result.status.type) {
      case 'success':
        state.forecastForReceiptLoadingState.state.status.type = 'success';
        state.forecastForReceiptLoadingState.type = 'none';
        toast.success(t('受領用内示から取引を外しました。'));
        await updateDisplayBusinesses();
        state.tableContents.checkedDisplayIds = [];
        break;
      case 'error':
      default:
        state.forecastForReceiptLoadingState.type = 'none';
        toast.error(t('受領用内示から取引を外せませんでした。'));
        break;
    }
  };

  //   ↑ 受領用内示編集モード

  // MARK: - Private

  /**
   * チェックが付いているアイテムを取得する
   * @param checkedDisplayIds - チェックされたアイテムのID
   */
  const _checkedItems = (checkedDisplayIds: string[]): IBusinessForCustomerForecast[] => {
    return state.items.filter((item) => checkedDisplayIds.includes(item.businessId));
  };

  /**
   * 仕入先内示をupsertで登録する
   * @param repository - リポジトリ
   * @param createdSupplierForecastYearMonth - 仕入先内示の作成年月
   * @param startTargetYearMonth - 内示対象月の開始月
   * @param targetYearMonthCount - 内示対象月の数
   */
  const _upsertConfirmSupplierAnnounce = async (
    repository: SupplierForecastRepository,
    createdSupplierForecastYearMonth: IYearMonth,
    startTargetYearMonth: IYearMonth,
    targetYearMonthCount: number
  ) => {
    const confirmResult = await openConfirmModal(
      'warning',
      t('仕入先内示の上書き(詳細ページ)'),
      t('確定'),
      t('キャンセル'),
      undefined,
      t('仕入先内示更新テキスト')
    );
    // 確定ボタン以外はスキップ
    if (!confirmResult.isConfirmed) return;

    // ローディング開始
    state.createSupplierForecastLoadingState.status.type = 'loading';

    const useCase = new BulkPostSupplierForecastUseCase();

    // チェックが付いているアイテムについて、客先内示=仕入先内示として登録。upsert処理を行う
    const result = await useCase.Execute(
      repository,
      createdSupplierForecastYearMonth,
      startTargetYearMonth,
      targetYearMonthCount,
      cachedFilterItems.value,
      true
    );

    switch (result.status.type) {
      case 'success':
        // ローディング終了
        state.createSupplierForecastLoadingState.status.type = 'success';
        // 成功トースト表示
        toast.success(t('ステータスを一括修正しました。'));
        // テーブルの再描画
        await updateDisplayBusinesses();
        break;

      case 'error':
        // その他のエラーの場合の処理
        state.createSupplierForecastLoadingState.status.type = 'error';
        console.error('createSupplierForecast error');
        // エラートースト表示
        toast.error(t('ステータスの一括修正に失敗しました。'));
        break;
    }
  };

  /**
   * サンプルExcelのダウンロード処理
   * @param selectedResponsibleUserId - 選択したユーザーID
   * @param startYearMonth - 開始年月
   */
  const getTemplateCustomerForecastExcelDownload = async (selectedResponsibleUserId: string, startYearMonth: IYearMonth) => {
    // モーダルを閉じる
    state.downloadForecastForReceiptModalState.modalState = false;

    const responsibleUserId: string = selectedResponsibleUserId.split(',')[1] ?? '';

    // 選択担当者IDが未選択の場合は処理を終了させる
    if (!responsibleUserId) {
      console.log('担当者が選択されていません');
      return;
    }
    // ローディング開始
    state.downloadForecastForReceiptModalState.loadingState.status.type = 'loading';

    // ダウンロード用の受領用内示情報を取得する
    const result = await openLoadingModal(t('出力中'), diContainer.getOutputForecastForReceiptExcelDataUseCase.Execute, [
      responsibleUserId,
    ]);

    // 取得したデータを元に、受領用内示のエクセルをダウンロードする
    switch (result.status.type) {
      case 'success':
        // ダウンロード情報をstate変数にセット
        // ダウンロードする受領用内示エクセルの作成を行う
        const downloadExcelUseCase = new DownloadForecastForReceiptExcelUseCase();
        const downloadResult = await downloadExcelUseCase.Execute(result.status.data, startYearMonth);
        saveAs(downloadResult, t('受領用内示.zip'));
        // ローディング終了
        state.downloadForecastForReceiptModalState.loadingState.status.type = 'success';
        // 成功トースト表示
        toast.success(t('ダウンロードしました'));
        break;

      case 'error':
        // その他のエラーの場合の処理
        state.downloadForecastForReceiptModalState.loadingState.status.type = 'error';
        // エラーモーダルを表示
        await openOkInformationModal('error', t('エラー'), 'OK', result.status.error.message);
        break;
    }
  };

  /**
   * 客先内示ダウンロードモーダルを開く
   * 開始月 = 前月、終了月 = 開始月 + 3ヶ月
   */
  const openDownloadCustomerForecastModal = () => {
    const startYearMonth = DateService.getShiftedIYearMonthFromNowDateByMonths(-1);

    // 終了月 = 開始月 + 3ヶ月
    const endYearMonth = DateService.getShiftedIYearMonthByMonths(startYearMonth.year, startYearMonth.month, 3);

    state.downloadCustomerForecastModalState = {
      modalState: true,
      startYearMonth: startYearMonth,
      endYearMonth: endYearMonth,
    };
  };

  /**
   * 客先内示ダウンロードモーダルを閉じる
   */
  const closeDownloadCustomerForecastModal = () => {
    state.downloadCustomerForecastModalState.modalState = false;
  };

  /**
   * 客先内示ダウンロードを実行する
   * @param startYearMonth - 開始年月
   * @param endYearMonth - 終了年月
   */
  const onApplyDownloadCustomerForecast = async (startYearMonth: IYearMonth, endYearMonth: IYearMonth) => {
    // Validate date range
    const startDate = new Date(startYearMonth.year, startYearMonth.month - 1);
    const endDate = new Date(endYearMonth.year, endYearMonth.month - 1);

    if (startDate > endDate) {
      toast.error(t('開始月は終了月より前に設定してください'));
      return;
    }

    try {
      // 受領用内示についてのフィルター項目を追加する
      const useFilterContentInfo = simpleCopy(cachedFilterItems.value);
      if (
        state.editForecastForReceiptModeState.selectedEditForecastForReceiptId !== '' &&
        state.editForecastForReceiptModeState.updateForecastForReceiptModalState != null
      ) {
        useFilterContentInfo.push(
          ForecastForReceiptMenuService.getFilterContentItem(state.editForecastForReceiptModeState.selectedEditForecastForReceiptId)
        );
      }

      // Convert filter items to API format
      const filterItem =
        useFilterContentInfo.length > 0 ? FilterService.convertCustomerForecastFilterItems(useFilterContentInfo) : undefined;

      // Call UseCase to export customer forecast data
      const result = await diContainer.exportCustomerForecastUseCase.Execute(
        startYearMonth.year,
        startYearMonth.month,
        endYearMonth.year,
        endYearMonth.month,
        state.receivedForecastYearMonthInfo.year,
        state.receivedForecastYearMonthInfo.month,
        i18n.locale,
        filterItem
      );

      if (result.status.type === 'error') {
        throw result.status.error;
      }

      // Get download URL and file name from response
      const { download_url, file_name } = result.status.data;

      // Trigger browser download
      const link = document.createElement('a');
      link.href = download_url;
      link.download = file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(t('ダウンロードを開始しました'));
      state.downloadCustomerForecastModalState.modalState = false;
    } catch (error) {
      console.error('Export error:', error);
      toast.error(t('ダウンロードに失敗しました'));
    }
  };

  return {
    monthsOptionsDisplayForecastNumber,
    state,
    $reset,
    tableContentItems,
    resetAllBusinessTableContentItems,
    resetTableCheckedState,
    init,
    fetchAllBusinessTableHeaders,
    fetchDisplayBusinesses,
    updateDisplayBusinesses,
    updateSelectedForecastForReceiptFormState,
    applyEditForecast,
    onSelectedDisplayForecastsMonth,
    createSupplierForecast,
    onChangePaging,
    sortTableContentItems,
    onAppliedDetailFilter,
    onApplyEditForecast,
    onSelectedReceiveMonth,
    onConfirmedCreateForecast,
    onCheckedDisplayIds,
    onAppliedConfirmApplyEdit,
    onCanceledConfirmApplyEdit,
    onRevokedConfirmApplyEdit,
    onSelectedForecastForReceiptMenu,
    onClosedForecastForReceiptActionModal,
    onAppliedCreateBaseBusinessForecastForReceipt,
    onAppliedAddBaseBusinessForecastForReceipt,
    onAppliedEditBaseBusinessForecastForReceipt,
    onAppliedDeleteBaseBusinessForecastForReceipt,
    onAppliedUpdateBaseBusinessForecastForReceipt,
    onSwitchedEditTargetForecastForReceipt,
    onClickedDeleteFromForecastForReceiptButton,
    onClickedDeleteForecastForReceiptInEditMode,
    onConfirmedDeleteForecastForReceiptInEditMode,
    onCanceledDeleteForecastForReceiptInEditMode,
    onClickRemoveFromForecastInRow,
    onBackToNormalMode,
    setNormalMode,
    getTemplateCustomerForecastExcelDownload,
    getFilterItemsValue,
    openDownloadCustomerForecastModal,
    closeDownloadCustomerForecastModal,
    onApplyDownloadCustomerForecast,
  };
});

/**
 * useBusinessListForecastForReceiptStoreの型
 */
export type UseBusinessListForecastForReceiptStore = ReturnType<typeof useBusinessListForecastForReceiptStore>;
